import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { Dumbbell, Activity, TrendingUp } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Analytics } from "@/lib/analytics";

const MS_PER_DAY = 86400000;

interface ExerciseData {
  exercise_name: string;
  workout_date: string;
  weight_kg: number | null;
  sets: number | null;
  reps: number | null;
  distance_km: number | null;
  duration_seconds: number | null;
  workout_type: string;
}

interface ExerciseProgressChartsProps {
  /** Free plan uses ~30 days; Premium passes a large window for “full history”. */
  historyDays?: number;
}

export const ExerciseProgressCharts = ({ historyDays = 30 }: ExerciseProgressChartsProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [exerciseData, setExerciseData] = useState<ExerciseData[]>([]);
  const [selectedExercise, setSelectedExercise] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [uniqueExercises, setUniqueExercises] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<string>('weight');

  useEffect(() => {
    Analytics.progressChartViewed('exercise');
    if (user) loadExerciseData();
  }, [user, historyDays]);

  // Real-time subscription for exercise updates
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('exercise-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'exercises'
        },
        () => {
          console.log('Exercise data changed, reloading...');
          loadExerciseData();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'workouts'
        },
        (payload) => {
          // Only reload if a workout was completed
          if (payload.new && payload.new.completed_at) {
            console.log('Workout completed, reloading exercise data...');
            loadExerciseData();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const loadExerciseData = async () => {
    if (!user) return;

    const cutoff = Date.now() - historyDays * MS_PER_DAY;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('exercises')
        .select(`
          name,
          sets,
          reps,
          weight_kg,
          distance_km,
          duration_seconds,
          workouts!inner(
            completed_at,
            created_at,
            workout_type,
            user_id
          )
        `)
        .eq('workouts.user_id', user.id)
        .not('workouts.completed_at', 'is', null)
        .order('completed_at', { referencedTable: 'workouts', ascending: true });


      if (error) throw error;

      const formattedData: ExerciseData[] = (data || [])
        .map((exercise) => ({
        exercise_name: exercise.name,
        workout_date: exercise.workouts.completed_at || exercise.workouts.created_at,
        weight_kg: exercise.weight_kg,
        sets: exercise.sets,
        reps: exercise.reps,
        distance_km: exercise.distance_km,
        duration_seconds: exercise.duration_seconds,
        workout_type: exercise.workouts.workout_type || 'strength'
      }))
        .filter((row) => new Date(row.workout_date).getTime() >= cutoff);


      setExerciseData(formattedData);
      
      const exercises = [...new Set(formattedData.map(d => d.exercise_name))];
      setUniqueExercises(exercises);
      
      if (exercises.length > 0 && !selectedExercise) {
        setSelectedExercise(exercises[0]);
      }
    } catch (error) {
      console.error('Error loading exercise data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getExerciseProgressData = (exerciseName: string) => {
    return exerciseData
      .filter(d => d.exercise_name === exerciseName)
      .map((d, index) => {
        const dist = d.distance_km != null ? Number(d.distance_km) : 0;
        const dur = d.duration_seconds != null ? Number(d.duration_seconds) : 0;
        return {
          session: index + 1,
          date: new Date(d.workout_date).toLocaleDateString(),
          weight: d.weight_kg != null ? Number(d.weight_kg) : 0,
          sets: d.sets != null ? Number(d.sets) : 0,
          reps: d.reps != null ? Number(d.reps) : 0,
          distance: dist,
          pace: dist > 0 ? (dur / 60) / dist : 0,
          workout_type: d.workout_type
        };
      });
  };

  const selectedExerciseData = selectedExercise ? getExerciseProgressData(selectedExercise) : [];
  const isRunningExercise = selectedExerciseData.length > 0 && 
    (selectedExerciseData[0].workout_type?.toLowerCase() === 'running' || selectedExercise.toLowerCase().includes('running'));

  // Ensure an active tab is always visible and defaults based on exercise type
  useEffect(() => {
    setActiveTab(isRunningExercise ? 'distance' : 'weight');
  }, [selectedExercise, exerciseData, isRunningExercise]);

  const derivedActiveTab = isRunningExercise 
    ? (activeTab === 'distance' || activeTab === 'pace' ? activeTab : 'distance')
    : (activeTab === 'weight' || activeTab === 'sets' || activeTab === 'reps' ? activeTab : 'weight');

  if (loading) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        {t('components.charts.exerciseProgress.loading')}
      </div>
    );
  }

  if (uniqueExercises.length === 0) {
    return (
      <Card className="bg-gradient-card border-0">
        <CardContent className="text-center py-8">
          <TrendingUp className="h-12 w-12 mx-auto mb-4 opacity-50 text-muted-foreground" />
          <p className="text-muted-foreground">{t('components.charts.exerciseProgress.emptyTitle')}</p>
          <p className="text-sm text-muted-foreground">{t('components.charts.exerciseProgress.emptyHint')}</p>
        </CardContent>
      </Card>
    );
  }

  const ChartTooltip = ({ active, payload, label, formatter }: {
    active?: boolean; payload?: any[]; label?: string;
    formatter?: (value: number) => string;
  }) => {
    if (!active || !payload?.length) return null;
    const value = payload[0]?.value ?? 0;
    const display = formatter ? formatter(value) : String(value);
    return (
      <div className="bg-background border border-border rounded-lg p-3 shadow-lg">
        <p className="font-medium text-sm mb-1">{label}</p>
        <p className="text-sm text-primary">{display}</p>
      </div>
    );
  };

  return (
    <Card className="bg-gradient-card border-0">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base font-normal">
          <TrendingUp className="h-5 w-5 text-primary" />
          {t('components.charts.exerciseProgress.title')}
        </CardTitle>
        <div className="mt-4">
          <Select value={selectedExercise} onValueChange={setSelectedExercise}>
            <SelectTrigger className="w-full max-w-sm">
              <SelectValue placeholder={t('components.charts.exerciseProgress.selectExercise')} />
            </SelectTrigger>
            <SelectContent>
              {uniqueExercises.map(exercise => (
                <SelectItem key={exercise} value={exercise}>
                  {exercise}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>

      <CardContent>
        {selectedExerciseData.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>{t('components.charts.exerciseProgress.emptyForExercise')}</p>
          </div>
        ) : isRunningExercise ? (
          <Tabs value={derivedActiveTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="distance" className="flex items-center gap-2">
                <Activity className="h-4 w-4" />
                {t('components.charts.exerciseProgress.tabDistance')}
              </TabsTrigger>
              <TabsTrigger value="pace" className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                {t('components.charts.exerciseProgress.tabPace')}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="distance">
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={selectedExerciseData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fontSize: 10 }} width={36} tickLine={false} axisLine={false} />
                    <Tooltip 
                      content={({ active, payload, label }) => (
                        <ChartTooltip 
                          active={active} payload={payload} label={label}
                          formatter={(v) => t('components.charts.exerciseProgress.valueKm', { value: v.toFixed(1) })}
                        />
                      )}
                      contentStyle={{ padding: 0, background: 'transparent', border: 'none', boxShadow: 'none' }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="distance" 
                      stroke="hsl(var(--accent))" 
                      strokeWidth={3}
                      dot={false}
                      activeDot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>

            <TabsContent value="pace">
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={selectedExerciseData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fontSize: 10 }} width={36} tickLine={false} axisLine={false} />
                    <Tooltip 
                      content={({ active, payload, label }) => (
                        <ChartTooltip 
                          active={active} payload={payload} label={label}
                          formatter={(v) => t('components.charts.exerciseProgress.valueMinPerKm', { value: v.toFixed(2) })}
                        />
                      )}
                      contentStyle={{ padding: 0, background: 'transparent', border: 'none', boxShadow: 'none' }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="pace" 
                      stroke="hsl(var(--gold))" 
                      strokeWidth={3}
                      dot={false}
                      activeDot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>
          </Tabs>
        ) : (
          <Tabs value={derivedActiveTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="weight" className="flex items-center gap-2">
                <Dumbbell className="h-4 w-4" />
                {t('components.charts.exerciseProgress.tabWeight')}
              </TabsTrigger>
              <TabsTrigger value="sets" className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                {t('components.charts.exerciseProgress.tabSets')}
              </TabsTrigger>
              <TabsTrigger value="reps" className="flex items-center gap-2">
                <Activity className="h-4 w-4" />
                {t('components.charts.exerciseProgress.tabReps')}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="weight">
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={selectedExerciseData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fontSize: 10 }} width={36} tickLine={false} axisLine={false} />
                    <Tooltip 
                      content={({ active, payload, label }) => (
                        <ChartTooltip 
                          active={active} payload={payload} label={label}
                          formatter={(v) => t('components.charts.exerciseProgress.valueKg', { value: v })}
                        />
                      )}
                      contentStyle={{ padding: 0, background: 'transparent', border: 'none', boxShadow: 'none' }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="weight" 
                      stroke="hsl(var(--primary))" 
                      strokeWidth={3}
                      dot={false}
                      activeDot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>

            <TabsContent value="sets">
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={selectedExerciseData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fontSize: 10 }} width={36} tickLine={false} axisLine={false} />
                    <Tooltip 
                      content={({ active, payload, label }) => (
                        <ChartTooltip 
                          active={active} payload={payload} label={label}
                          formatter={(v) => t('components.charts.exerciseProgress.valueSets', { value: v })}
                        />
                      )}
                      contentStyle={{ padding: 0, background: 'transparent', border: 'none', boxShadow: 'none' }}
                    />
                    <Bar 
                      dataKey="sets" 
                      fill="hsl(var(--gold))" 
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>

            <TabsContent value="reps">
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={selectedExerciseData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fontSize: 10 }} width={36} tickLine={false} axisLine={false} />
                    <Tooltip 
                      content={({ active, payload, label }) => (
                        <ChartTooltip 
                          active={active} payload={payload} label={label}
                          formatter={(v) => t('components.charts.exerciseProgress.valueReps', { value: v })}
                        />
                      )}
                      contentStyle={{ padding: 0, background: 'transparent', border: 'none', boxShadow: 'none' }}
                    />
                    <Bar 
                      dataKey="reps" 
                      fill="hsl(var(--accent))" 
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </CardContent>
    </Card>
  );
};